/** Row-open pre-filters the panel to this user; header-open passes null/undefined. */
export interface ActivityFeedInitialUser {
  id: string | number;
  name: string;
}

/** Shape a future export/API call would send for the Users scope — "everyone
 * except these" scales across pagination/search, unlike shipping every id. */
export type UserSelectionPayload =
  | { selectAll: true; excludedUserIds: string[] }
  | { selectAll: false; userIds: string[] };

// strictNullChecks is off project-wide, which breaks inline discriminant
// narrowing on `UserSelectionPayload` — an explicit predicate narrows fine.
export const isInclusionPayload = (
  p: UserSelectionPayload
): p is Extract<UserSelectionPayload, { selectAll: false }> =>
  p.selectAll === false;

export type ActivityEventTypeId =
  | 'signed_in'
  | 'page_viewed'
  | 'search'
  | 'alert_viewed'
  | 'alert_acknowledged'
  | 'alert_resolved'
  | 'alert_dismissed'
  | 'comment_viewed'
  | 'comment_added'
  | 'notification_viewed'
  | 'notification_added_edited'
  | 'report_viewed'
  | 'report_downloaded'
  | 'insight_chatbot_opened'
  | 'help_opened'
  | 'messages_opened';

export interface EventTypeChild {
  id: ActivityEventTypeId;
  label: string;
}

export interface EventTypeGroup {
  /** For flat groups (no children) this doubles as the leaf id. */
  id: string;
  label: string;
  children?: EventTypeChild[];
}

export interface ActivityEvent {
  id: string;
  userId: string;
  /** Actor's display name, carried on the event itself rather than joined
   * client-side — the Users filter's own fetched list won't necessarily
   * contain every user an event's userId refers to (e.g. dummy data). */
  userName: string;
  typeId: ActivityEventTypeId;
  /** Bold verb phrase, e.g. "signed in", "dismissed alert". */
  action: string;
  /** One-line detail per the PRD's per-type content template. */
  detail: string;
  /** Day-group label this event belongs to (matches its bucket's own `date`)
   * and its display time — both sent pre-formatted by the backend, no
   * frontend timezone math needed. */
  date: string;
  time: string;
  /** Null when the event isn't tied to a shipment (e.g. sign-in, page view). */
  shipment_id: string | null;
  coc_id: string | null;
  /** "customer_leg" / "execution_leg" when the row is a leg view, else null. */
  leg_type?: string | null;
  /** Set on an execution-leg row, which has no shipment_id. */
  execution_leg_id?: string | null;
  /** Which write path produced a notification_added_edited row (e.g.
   * "company_alert", "shipment_notification"); null for other event types. */
  notification_source?: string | null;
}

export type TimeWindowPreset = '24h' | '7d' | '30d' | 'custom';

export interface TimeWindowValue {
  preset: TimeWindowPreset;
  /** ISO date strings (yyyy-mm-dd), custom preset only. */
  start?: string;
  end?: string;
}

export type TimezoneMode = 'browser' | 'subject';

/** Snapshot captured right before navigating away to a shipment, and handed
 * back on return so the panel reopens exactly as the user left it instead
 * of resetting to defaults. */
export interface ActivityFeedRestoreState {
  /** Full selection — including a partial multi-select with exclusions,
   * which `singleUser` alone can't express. Source of truth for restore. */
  selection: UserSelectionPayload;
  /** Display-name hint for the single-user case only, so the dropdown can
   * show their name immediately even before that page of users has loaded. */
  singleUser: ActivityFeedInitialUser | null;
  selectedEventTypeIds: string[];
  timeWindow: TimeWindowValue;
  tzMode: TimezoneMode;
  /** id of the activity row nearest the top of the scrolled view when the
   * user clicked away — anchoring to the row itself survives re-fetched data
   * shifting slightly, unlike a raw pixel offset. Null when at the top. */
  topActivityId: string | null;
}

export interface DayGroup {
  key: string;
  label: string;
  events: ActivityEvent[];
}
